<template>
    <div class="operations-insights-page">
        <main>
            <h1 class="visually-hidden">Operations Insights Dashboard</h1>

            <section aria-labelledby="filters-heading">
                <h2 id="filters-heading" class="visually-hidden">Filters</h2>
                <FilterBar :page="preferenceKey" :items="filterConfig" @apply="onApply" :enable-views="true" :title="'Operations Management'" />
            </section>

            <!-- Display this info box if API returns an empty array -->
            <section v-if="isWsDefOperationConfigRefDataEmpty" class="info-box-wrapper">
                <al-message type="info" class="info-box">
                    <div>
                        No workstream definitions are currently enabled for the Operations Management view. To resolve this, please
                        <span v-if="!showAdmin">contact your administrator to</span>
                        configure workstreams in the Dashboard Administration settings.
                    </div>
                </al-message>
                <DashboardAdminLink v-if="showAdmin" />
            </section>

            <!-- Only display the section if ref data is not empty -->
            <section v-if="!isWsDefOperationConfigRefDataEmpty" class="tabs-section">
                <div class="tabs-header">
                    <DashboardAdminLink v-if="showAdmin" />
                </div>
                <tabs :options="{ useUrlFragment: false }">
                    <tab v-if="acl('view_backlog_insights')" name="Backlog Insights">
                        <BacklogInsightsTab :query-params="queryParams" />
                    </tab>
                    <tab v-if="acl('view_workstream_assignments_insights')" name="Workstream Assignments Insights">
                        <WorkstreamAssignmentsInsightsTab :query-params="queryParams" />
                    </tab>
                </tabs>
            </section>
        </main>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

// Components
import BacklogInsightsTab from '@/pages/OperationsInsights/components/BacklogInsightsTab.vue';
import WorkstreamAssignmentsInsightsTab from '@/pages/OperationsInsights/components/WorkstreamAssignmentsInsightsTab.vue';
import { Tabs, Tab } from '@agyletime/vue-3-common/src/components/Tabs';

// Composables
import { useProvidePagePreferences } from '@agyletime/vue-3-common/src/composables/useProvidePagePreferences';
import { useFilterConfig } from '@/pages/OperationsInsights/composables/useFilterConfig';
import { useACLs } from '@agyletime/vue-3-common/src/composables/acl';
import { isOperationsAdministrationEnabled } from '@/pages/OperationsAdministration/utils/isOperationsAdministrationEnabled';
import { useActiveWsDefOperationConfigRefDataStore } from '@/stores/activeWsDefOperationConfigRefDataStore';
import DashboardAdminLink from '@/pages/OperationsInsights/components/DashboardAdminLink.vue';

// ACL setup
const { acl } = useACLs();

// Filter setup
// IMPORTANT: Set up pagePreferences. There's code in vue-3-common that stops the `@apply` event from firing if we don't have it.
const preferenceKey = 'operations-insights-page';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const pagePreferences = useProvidePagePreferences(preferenceKey, false); // pagePreference is actually used by FilterBar deep down. Linting just doesn't know to check that.
const { filterConfig, queryParams, onApply } = useFilterConfig();

// Check if workstream definitions are empty
const { activeWsDefOperationConfigRefDataStore } = useActiveWsDefOperationConfigRefDataStore();
const showAdmin = isOperationsAdministrationEnabled(acl);
const isWsDefOperationConfigRefDataEmpty = computed(
    () => activeWsDefOperationConfigRefDataStore.loaded && activeWsDefOperationConfigRefDataStore.data.length === 0
);
</script>

<style scoped lang="scss">
@use '@/styles/commonPageStyles.scss';

.visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.grid-container {
    max-width: unset;
}

.tabs-section {
    position: relative;
}

.tabs-header {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 1rem;

    position: absolute;
    right: 0;
    top: 0.5rem;
}
</style>
